import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OUTPUT_STATUS_TONE, STREAM_PRIVACIES, STREAM_PRIVACY_LABEL, type StreamPrivacy } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import type { BroadcastData } from '../lib/streaming';
import { Button, Card, CardHeader, ErrorText, Field, Input, Pill, Segmented, Textarea } from './ui';
import { EgressOverlay } from './EgressOverlay';

const Notice = ({ children }: { children: string }) => <p role="status" className="m-0 rounded-lg bg-warn-bg px-3 py-2 text-sm text-warn">{children}</p>;

/**
 * The public broadcast of a webinar (INT-STR-02…04, INT-STR-06), for its host, co-hosts and Admins: destinations, title,
 * description and privacy; then, once live, each destination's health (connecting / live / error) with its own start and
 * stop, the full-disclaimer slates, and a preview of the overlay viewers see.
 */
export function BroadcastPanel({ webinarId, title, description }: { webinarId: string; title: string; description: string | null }) {
  const qc = useQueryClient();
  const key = ['broadcast', webinarId];
  const { data, error } = useQuery({ queryKey: key, queryFn: () => api<BroadcastData>(`/webinars/${webinarId}/broadcast`), refetchInterval: (q) => (q.state.data?.webinarStatus === 'LIVE' ? 5_000 : 30_000) });
  const [form, setForm] = useState<{ title: string; description: string; privacy: StreamPrivacy; destinationIds: string[] } | null>(null);
  const [fb, setFb] = useState<string | null>(null);
  const save = useMutation({ mutationFn: (body: object) => api<BroadcastData>(`/webinars/${webinarId}/broadcast`, { method: 'PUT', body }), onSuccess: (d) => { qc.setQueryData(key, d); setForm(null); } });
  const act = useMutation({ mutationFn: (p: { path: string; body?: object; method?: string }) => api<BroadcastData>(`/webinars/${webinarId}/broadcast/${p.path}`, { method: p.method, body: p.body ?? {} }), onSuccess: (d) => qc.setQueryData(key, d) });
  if (error) return <Card className="p-4 text-sm text-down">{errorText(error)}</Card>;
  if (!data) return null;
  const b = data.broadcast;
  const f = form ?? { title: b?.title ?? title.slice(0, 100), description: b?.description ?? description ?? '', privacy: b?.privacy ?? 'PUBLIC', destinationIds: b?.outputs.map((o) => o.destination.id) ?? [] };
  const set = (patch: Partial<typeof f>) => setForm({ ...f, ...patch });
  const open = data.webinarStatus === 'SCHEDULED' || data.webinarStatus === 'LIVE';
  const live = data.webinarStatus === 'LIVE';
  const busy = new Set(b?.outputs.filter((o) => ['CONNECTING', 'LIVE', 'ERROR'].includes(o.status)).map((o) => o.destination.id));
  return (
    <Card>
      <CardHeader title="Public broadcast" right={b && <span className="text-xs text-muted">{STREAM_PRIVACY_LABEL[b.privacy]}</span>} />
      <div className="flex flex-col gap-4 p-4">
        {!data.setup.egress.configured && <Notice>{data.setup.egress.message!}</Notice>}
        {data.setup.egress.configured && !data.setup.recording.configured && <Notice>{data.setup.recording.message!}</Notice>}

        {b && b.outputs.length > 0 && (
          <section aria-label="Destinations" className="flex flex-col gap-2">
            {b.outputs.map((o) => (
              <div key={o.id} className="flex flex-col gap-1 rounded-lg bg-raised px-3 py-2.5" data-testid={`output-${o.destination.name}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex flex-wrap items-center gap-2">
                    <strong className="text-sm">{o.destination.name}</strong>
                    <span className="text-xs text-muted">{o.destination.kindLabel}{o.destination.streamKey ? ` · key ${o.destination.streamKey}` : ''}</span>
                    <Pill tone={OUTPUT_STATUS_TONE[o.status]}>{o.status === 'LIVE' ? '● Live' : o.statusLabel}</Pill>
                    {o.status === 'ERROR' && o.attempts > 0 && <span className="text-xs text-muted">{o.attempts} failed start{o.attempts > 1 ? 's' : ''} · retrying automatically</span>}
                  </span>
                  {live && (
                    <span className="flex gap-2">
                      {o.canStart && <Button aria-label={`Start ${o.destination.name}`} onClick={() => act.mutate({ path: `outputs/${o.id}/start` })} disabled={act.isPending || o.destination.removed}>Start</Button>}
                      {o.canStop && <Button variant="danger" aria-label={`Stop ${o.destination.name}`} onClick={() => act.mutate({ path: `outputs/${o.id}/stop` })} disabled={act.isPending}>Stop</Button>}
                    </span>
                  )}
                </div>
                {o.lastError && o.status === 'ERROR' && <span className="text-xs break-words text-down">{o.lastError}</span>}
                {o.videoUrl && <a href={o.videoUrl} target="_blank" rel="noreferrer" className="text-xs break-all">{o.videoUrl}</a>}
              </div>
            ))}
            {live && <span className="text-xs text-muted">Each destination starts and stops on its own. A failing one never stops the webinar, the recording or the others.</span>}
          </section>
        )}

        {live && b && (
          <div className="flex flex-col gap-2">
            <span className="text-xs text-muted">Full-disclaimer slate on the stream</span>
            <Segmented label="Slate" options={[{ value: 'NONE', label: 'No slate' }, { value: 'START', label: 'Start slate' }, { value: 'END', label: 'End slate' }]} value={b.slate ?? 'NONE'} onChange={(v) => act.mutate({ path: 'slate', body: { slate: v === 'NONE' ? null : v } })} />
          </div>
        )}
        <ErrorText error={act.error} />

        {open && (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate({ ...f, description: f.description.trim() || null });
            }}
          >
            <Field label="Broadcast title (YouTube, 100 characters)"><Input value={f.title} onChange={(e) => set({ title: e.target.value })} maxLength={100} required /></Field>
            <Field label="Description" hint="The full disclaimer is added to the end automatically."><Textarea value={f.description} onChange={(e) => set({ description: e.target.value })} rows={3} maxLength={4500} /></Field>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted">Privacy</span>
              <Segmented label="Privacy" options={STREAM_PRIVACIES.map((p) => ({ value: p, label: STREAM_PRIVACY_LABEL[p] }))} value={f.privacy} onChange={(privacy) => set({ privacy })} />
            </div>
            <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
              <legend className="mb-1 text-xs text-muted">Stream to</legend>
              {!data.destinations.length && <span className="text-sm text-sub">No streaming destinations yet. An Admin adds them under Administration → Streaming.</span>}
              {data.destinations.map((d) => (
                <label key={d.id} className="flex min-h-9 items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.destinationIds.includes(d.id)} disabled={busy.has(d.id)} onChange={(e) => set({ destinationIds: e.target.checked ? [...f.destinationIds, d.id] : f.destinationIds.filter((x) => x !== d.id) })} />
                  {d.name} <span className="text-xs text-muted">{d.kindLabel}{d.streamKey ? ` · ${d.streamKey}` : ''}{d.channel?.handle ? ` · ${d.channel.handle}` : ''}</span>
                </label>
              ))}
            </fieldset>
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="primary" type="submit" disabled={save.isPending || f.title.trim().length < 3}>{save.isPending ? 'Saving…' : b ? 'Save broadcast' : 'Set up the broadcast'}</Button>
              {!live && <span className="text-xs text-muted">Chosen destinations start when you go live.</span>}
              {live && <span className="text-xs text-muted">Newly added destinations wait for Start.</span>}
            </div>
            <ErrorText error={save.error} />
          </form>
        )}

        {data.overlay && (
          <details className="text-sm">
            <summary className="cursor-pointer text-sub">Preview the stream overlay</summary>
            <div className="relative mt-2 aspect-video w-full max-w-xl overflow-hidden rounded-lg bg-[#1b222b]" data-testid="overlay-preview">
              <EgressOverlay overlay={data.overlay} slate={data.overlay.slate} />
            </div>
          </details>
        )}

        {!open && (
          <section aria-label="Links" className="flex flex-col gap-2 text-sm">
            <span>YouTube video: {data.links.youtube ? <a href={data.links.youtube} target="_blank" rel="noreferrer">{data.links.youtube}</a> : <span className="text-muted">none</span>}</span>
            <form
              className="flex flex-wrap items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                act.mutate({ path: 'links', method: 'PUT', body: { facebookUrl: (fb ?? data.links.facebook ?? '').trim() || null } });
              }}
            >
              <Field label="Facebook video link (paste it from the Page, optional)" className="min-w-0 flex-1"><Input value={fb ?? data.links.facebook ?? ''} onChange={(e) => setFb(e.target.value)} placeholder="https://www.facebook.com/…/videos/…" /></Field>
              <Button type="submit" disabled={act.isPending}>Save link</Button>
            </form>
            <span className="text-xs text-muted">Links are shown on the webinar and its recording in the <Link to="/library">library</Link>.</span>
          </section>
        )}
        {data.setup.youtube.configured ? null : <span className="text-xs text-muted">{data.setup.youtube.message}</span>}
      </div>
    </Card>
  );
}
